// Tabs and apps. A tab is a destination; an app is a named, ordered bundle of
// tab keys. The nav rail shows the current app's tabs; every tab, in any app or
// none, stays reachable from the app launcher, the command palette and the All
// tabs page. A team-only person (Delivery - External) is offered only the tabs
// and apps marked `team`; the server refuses the rest anyway.

import {
  AssistantIcon,
  AudienceIcon,
  ContactIcon,
  DashboardIcon,
  DocumentIcon,
  HomeIcon,
  InvoiceIcon,
  type NavNode,
  OpportunityIcon,
  PaceIcon,
  PartIcon,
  ProjectIcon,
  ReportIcon,
  ScheduleIcon,
  SetupIcon,
  TargetIcon,
  WorkOrderIcon,
} from '@olytic/foundation';
import type { ReactNode } from 'react';

import { DELIVERY_HOME_ROUTE, HOME_ROUTE, LIBRARY_ROUTE, PRODUCT_CATALOG_ROUTE, type ObjectKey, OBJECTS, REPORTS_ROUTE, RESOURCING_ROUTE, SELLER_HOME_ROUTE, SETUP_ROUTES, setupPagesFor, WORK_ROUTE } from './navigation';
import { OBJECT_DEFS } from './objects/objectDefs';
import { SETTINGS_ROUTE } from './profile/routes';
import type { ProjectAccess } from './auth/api';

export const TABS_ROUTE = '/tabs';

/** Icons for setup's pages; AI management's pages share the assistant icon. */
export const SETUP_ICONS: Readonly<Record<string, ReactNode>> = {
  'setup-home': <SetupIcon />,
  'setup-users': <ContactIcon />,
  'setup-objects': <DashboardIcon />,
};

/**
 * Setup's navigation for someone with this role key, as an app's tabs: flat and
 * label-only under the Setup parent, without the Admin-only pages for anyone
 * else. A grouped page carries its group in its label ("AI usage").
 */
export function setupNavigation(role: string | undefined): NavNode[] {
  return setupPagesFor(role).map((page) => ({
    key: page.key,
    label: page.href === SETUP_ROUTES.home ? 'Home' : page.group === 'AI management' ? `AI ${page.label.charAt(0).toLowerCase()}${page.label.slice(1)}` : page.label,
    icon: SETUP_ICONS[page.key],
    href: page.href,
  }));
}

interface TabBase {
  /** Unique across every kind. An object tab's key is its object key. */
  key: string;
  label: string;
  icon: ReactNode;
  href: string;
  /** A team-only person may open it: it calls only the routes they may call. */
  team?: boolean;
}

/** An object's list page. Every object has exactly one, derived below. */
export interface ObjectTab extends TabBase {
  kind: 'object';
  object: ObjectKey;
}

/** A bespoke page built in code: not a record page or a list view. */
export interface PageTab extends TabBase {
  kind: 'page';
}

/** A saved report as a tab. Not built yet: saved reports aren't persisted. */
export interface ReportTab extends TabBase {
  kind: 'report';
  reportId: string;
}

export type TabDef = ObjectTab | PageTab | ReportTab;
export type TabKind = TabDef['kind'];

export const TAB_KINDS: readonly { kind: TabKind; label: string; singular: string }[] = [
  { kind: 'object', label: 'Objects', singular: 'Object' },
  { kind: 'page', label: 'Pages', singular: 'Page' },
  { kind: 'report', label: 'Reports', singular: 'Report' },
];

/** One tab per object, from the object definitions, so a new object gets its tab. */
export const OBJECT_TABS: readonly ObjectTab[] = Object.values(OBJECT_DEFS).map((def) => ({
  kind: 'object',
  key: def.key,
  object: def.key,
  label: def.label,
  icon: def.icon,
  href: OBJECTS.find((o) => o.key === def.key)?.href ?? `/${def.key}`,
}));

/**
 * Bespoke pages. Register a new one here (and its route in App.tsx), e.g. a
 * quote line editor, then add its key to the apps that should show it.
 */
export const PAGE_TABS: readonly PageTab[] = [
  { kind: 'page', key: 'seller-home', label: 'Seller Home', icon: <TargetIcon />, href: SELLER_HOME_ROUTE },
  { kind: 'page', key: 'sales-pulse', label: 'Sales Pulse', icon: <HomeIcon />, href: HOME_ROUTE },
  { kind: 'page', key: 'reports', label: 'Reports', icon: <ReportIcon />, href: REPORTS_ROUTE },
  { kind: 'page', key: 'resourcing', label: 'Resource Planning', icon: <ScheduleIcon />, href: RESOURCING_ROUTE },
  { kind: 'page', key: 'work', label: 'Work', icon: <WorkOrderIcon />, href: WORK_ROUTE, team: true },
  { kind: 'page', key: 'delivery-home', label: 'My Delivery', icon: <PaceIcon />, href: DELIVERY_HOME_ROUTE, team: true },
  { kind: 'page', key: 'library', label: 'Library', icon: <DocumentIcon />, href: LIBRARY_ROUTE },
  { kind: 'page', key: 'product-catalog', label: 'Product Catalog', icon: <PartIcon />, href: PRODUCT_CATALOG_ROUTE },
];

/** Saved reports pinned as tabs. Empty until saved reports are persisted. */
export const REPORT_TABS: readonly ReportTab[] = [];

export const TABS: readonly TabDef[] = [...OBJECT_TABS, ...PAGE_TABS, ...REPORT_TABS];

const TAB_BY_KEY = new Map(TABS.map((t) => [t.key, t]));

export function tabByKey(key: string): TabDef | undefined {
  return TAB_BY_KEY.get(key);
}

export interface AppDef {
  key: string;
  label: string;
  icon: ReactNode;
  description: string;
  /** Tab keys, in nav order. */
  tabs: readonly string[];
  /** Offered to a team-only person, with only their tabs. */
  team?: boolean;
}

export const APPS: readonly AppDef[] = [
  {
    key: 'sales',
    label: 'Sales',
    icon: <OpportunityIcon />,
    description: 'Leads, accounts and the pipeline',
    tabs: ['seller-home', 'sales-pulse', 'leads', 'accounts', 'contacts', 'opportunities', 'quotes', 'revenue', 'meetings', 'audiences', 'activities', 'documents', 'reports'],
  },
  {
    key: 'delivery',
    label: 'Delivery',
    icon: <ProjectIcon />,
    description: 'Resource planning, work, projects, time and meetings',
    tabs: [
      'delivery-home', 'resourcing', 'work', 'projects', 'resource-assignments', 'resource-capacity', 'resource-roles',
      'assets', 'time-entries', 'meetings', 'accounts', 'documents', 'reports',
    ],
    team: true,
  },
  {
    key: 'leadership',
    label: 'Leadership',
    icon: <DashboardIcon />,
    description: 'The pipeline, delivery and who is booked',
    tabs: ['leads', 'accounts', 'opportunities', 'contacts', 'quotes', 'delivery-home', 'work', 'resourcing', 'meetings'],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    icon: <AudienceIcon />,
    description: 'Audiences, leads, outreach and the research library',
    tabs: [
      'sales-pulse', 'audiences', 'leads', 'agent-conversations', 'contacts', 'meetings', 'activities',
      'research-sources', 'research-findings', 'research-figures', 'reports',
    ],
  },
  {
    key: 'products',
    label: 'Products',
    icon: <PartIcon />,
    description: 'The catalog, its price list and how it is priced and delivered',
    tabs: ['product-catalog', 'products', 'product-groups', 'prices', 'product-options', 'price-grids', 'guidelines', 'deliverable-types', 'deliverables', 'meetings', 'reports'],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: <InvoiceIcon />,
    description: 'Invoices, billing schedules and billing policies',
    tabs: ['sales-pulse', 'invoices', 'invoice-runs', 'billing-schedules', 'billing-accounts', 'billing-policies', 'contracts', 'reports'],
  },
  {
    key: 'library',
    label: 'Library',
    icon: <DocumentIcon />,
    description: 'The building blocks every Olytic app is made from',
    tabs: ['library'],
  },
];

export const DEFAULT_APP_KEY = 'sales';

export function appByKey(key: string): AppDef | undefined {
  return APPS.find((a) => a.key === key);
}

/** The app's tabs, in its order. */
export function tabsOfApp(app: AppDef): TabDef[] {
  return app.tabs.flatMap((key) => {
    const tab = tabByKey(key);
    return tab ? [tab] : [];
  });
}

// ── Access ──────────────────────────────────────────────────────────────────

/** Whether someone with this access may open the tab. */
export function mayOpenTab(tab: TabDef, access: ProjectAccess): boolean {
  return access === 'all' || tab.team === true;
}

/** The tabs someone may open. */
export function tabsFor(access: ProjectAccess): TabDef[] {
  return TABS.filter((t) => mayOpenTab(t, access));
}

/** The apps someone may use, each with only the tabs they may open. */
export function appsFor(access: ProjectAccess): AppDef[] {
  if (access === 'all') return [...APPS];
  return APPS.filter((a) => a.team).map((a) => ({ ...a, tabs: a.tabs.filter((key) => tabByKey(key)?.team) }));
}

/**
 * The page's tab when it is not one of the app's own: a page opened from
 * outside the app shows in the rail, marked temporary, while it is open.
 */
export function temporaryTab(app: AppDef, activeKey: string, access: ProjectAccess): TabDef | undefined {
  if (!activeKey || app.tabs.includes(activeKey)) return undefined;
  const tab = tabByKey(activeKey);
  return tab && mayOpenTab(tab, access) ? tab : undefined;
}

/** The apps that include a tab, in app order. */
export function appsWithTab(tabKey: string): AppDef[] {
  return APPS.filter((a) => a.tabs.includes(tabKey));
}

// ── Current app, remembered per browser ─────────────────────────────────────

const CURRENT_APP_STORAGE_KEY = 'ops.currentApp';

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>;

function browserStorage(): KeyValueStore | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** The remembered app, or Sales when nothing valid is stored or storage is unavailable. */
export function loadCurrentApp(storage: KeyValueStore | undefined = browserStorage()): string {
  try {
    const stored = storage?.getItem(CURRENT_APP_STORAGE_KEY);
    if (stored && appByKey(stored)) return stored;
  } catch {
    // Blocked storage: fall through to the default.
  }
  return DEFAULT_APP_KEY;
}

/** The app to show from those offered: the chosen one if offered, else the first. */
export function currentAppOf(key: string, offered: readonly AppDef[]): AppDef {
  return offered.find((a) => a.key === key) ?? offered.find((a) => a.key === DEFAULT_APP_KEY) ?? offered[0] ?? (appByKey(DEFAULT_APP_KEY) as AppDef);
}

export function saveCurrentApp(key: string, storage: KeyValueStore | undefined = browserStorage()): void {
  try {
    storage?.setItem(CURRENT_APP_STORAGE_KEY, key);
  } catch {
    // Not remembered; the switch still applies for this visit.
  }
}

// ── Command palette ─────────────────────────────────────────────────────────

export interface CommandEntry {
  /** Unique across the palette. */
  id: string;
  label: string;
  group: string;
  icon?: ReactNode;
  /** Present for a destination. */
  href?: string;
  /** Present for an app: selecting it switches to that app. */
  appKey?: string;
  /** Extra words the palette matches without showing them. */
  keywords?: string;
}

/**
 * Every app, every tab of every kind, the All tabs page and setup's pages, as far
 * as `access` allows; an Admin-only setup page only for the `role` key 'admin'.
 */
export function commandEntries(access: ProjectAccess, role: string | undefined): CommandEntry[] {
  const team = access !== 'all';
  return [
    ...appsFor(access).map((a) => ({
      id: `app:${a.key}`,
      label: `${a.label} app`,
      group: 'Apps',
      icon: a.icon,
      appKey: a.key,
      keywords: `switch app ${a.description}`,
    })),
    ...TAB_KINDS.flatMap(({ kind, label }) =>
      tabsFor(access).filter((t) => t.kind === kind).map((t) => ({
        id: t.key,
        label: t.label,
        group: label,
        icon: t.icon,
        href: t.href,
      })),
    ),
    { id: 'all-tabs', label: 'All tabs', group: 'Pages', icon: <DashboardIcon />, href: TABS_ROUTE },
    ...(team ? [] : [{ id: 'reports-new', label: 'New report', group: 'Pages', icon: <ReportIcon />, href: `${REPORTS_ROUTE}/new` }]),
    {
      id: 'your-settings',
      label: 'Your settings',
      group: 'Pages',
      icon: <ContactIcon />,
      href: SETTINGS_ROUTE,
      keywords: 'profile photo preferences appearance week',
    },
    ...(team ? [] : setupPagesFor(role)).map((p) => ({
      id: p.key,
      label: p.group ? `${p.group}: ${p.label}` : p.label,
      group: 'Setup',
      icon: SETUP_ICONS[p.key] ?? <AssistantIcon />,
      href: p.href,
      keywords: 'setup admin settings',
    })),
  ];
}
